import { useQuery } from 'react-query';
import { chamarOktonBank } from '../oktonbank';

// Uma Moeda de Cheque de Terceiro. Não aparece entre as Moedas da Conta
// porque não se vincula a nenhuma: o cheque é um papel em mãos, e só entra
// numa Conta quando for depositado.
export interface MoedaCheque {
  id_moe: string;
  nome_moe: string;
  tipodocumento_tpt: string;
}

// Títulos já baixados na Conta. Só título baixado tem moeda_fin preenchida, e
// sem moeda_fin não há como saber a que Conta ele pertence.
// tipo_fin é '1' (Receber) ou '2' (Pagar), e valorbaixa_fin é sempre positivo:
// o sentido do dinheiro está no tipo, não no sinal.
export interface LancamentoFinanceiro {
  id_fin: string;
  filial_fin: string;
  documento_fin: string | null;
  baixa_fin: string;
  vencimento_fin: string | null;
  tipo_fin: string;
  valorbaixa_fin: string;
  observacao_fin: string | null;
  conciliado_fin: string;
  moeda_fin: string;
  // A Moeda da baixa é a Moeda do banco que liquidou o título.
  nome_moe: string | null;
  centrocusto_fin: string | null;
  departamento_fin: string | null;
  orientador_fin: string | null;
  nome_cdc: string | null;
  nome_cli: string | null;
  // Só as linhas de cheque trazem estes: o papel não é movimento bancário,
  // e o que interessa nele é o banco sacado e a situação.
  ehcheque_fin?: string;
  status_fin?: string;
  nome_ban?: string | null;
}

// status_fin do IFLEX, no que alcança um cheque.
export const STATUS_CHEQUE: Record<string, string> = {
  '1': 'Em carteira',
  '5': 'Devolvido',
  '6': 'Compensado',
  '8': 'Depositado',
};

interface IResponseData {
  rows: LancamentoFinanceiro[];
}

export const TIPO_FIN_RECEBER = '1';
export const TIPO_FIN_PAGAR = '2';

async function getLancamentosFinanceiros(
  idConta?: string,
  status: 'pending' | 'all' = 'pending',
  dataIni = '',
  dataFim = '',
  especie = 'banco',
): Promise<LancamentoFinanceiro[]> {
  const dados = await chamarOktonBank<IResponseData>('OBFinanceiro', 'listar', {
    conta: idConta || '',
    status,
    dataini: dataIni,
    datafim: dataFim,
    especie,
  });
  return Array.isArray(dados?.rows) ? dados.rows : [];
}

// A Conta é opcional: sem ela o back usa as Moedas de todas as Contas
// Bancárias da Filial, que é o que a tela chama de "Todas as contas".
/**
 * O que a listagem traz.
 *
 * 'banco' são os movimentos bancários, que é o que a tela sempre mostrou.
 * 'cheque' são os papéis em carteira: eles não têm baixa e não passaram por
 * Conta nenhuma até serem depositados, então saem de outra consulta e o
 * filtro de Conta não se aplica a eles.
 */
export type EspecieLancamento = 'banco' | 'cheque' | 'all';

export function useLancamentosFinanceiros(
  idConta?: string,
  status: 'pending' | 'all' = 'pending',
  dataIni = '',
  dataFim = '',
  especie: EspecieLancamento = 'banco',
  habilitado = true,
) {
  return useQuery(
    [
      'lancamentos-financeiros',
      idConta ?? '',
      status,
      dataIni,
      dataFim,
      especie,
    ],
    () => getLancamentosFinanceiros(idConta, status, dataIni, dataFim, especie),
    { enabled: habilitado },
  );
}

// Títulos que ainda não foram baixados. Diferente dos baixados, estes não têm
// Moeda nem Conta: moeda_fin só é preenchida na baixa, e é ela que diz em qual
// Conta o dinheiro cai. Por isso a lista não é filtrada por Conta, e o período
// olha o vencimento.
export interface LancamentoAberto {
  id_fin: string;
  filial_fin: string;
  documento_fin: string | null;
  emissao_fin: string | null;
  vencimento_fin: string;
  tipo_fin: string;
  valor_fin: string;
  // saldo_fin é o que ainda falta receber ou pagar, e é por ele que o IFLEX
  // decide que o título está em aberto. saldoliq_fin vem junto mas fica
  // zerada na maioria dos títulos: não serve para mostrar nem para somar.
  saldo_fin: string;
  saldoliq_fin: string;
  observacao_fin: string | null;
  centrocusto_fin: string | null;
  departamento_fin: string | null;
  orientador_fin: string | null;
  movimento_fin: string | null;
  nome_cdc: string | null;
  nome_cli: string | null;
}

export function useLancamentosAbertos(dataIni = '', dataFim = '') {
  return useQuery(['lancamentos-abertos', dataIni, dataFim], async () => {
    const dados = await chamarOktonBank<{ rows: LancamentoAberto[] }>(
      'OBFinanceiro',
      'listarAbertos',
      { dataini: dataIni, datafim: dataFim },
    );
    return Array.isArray(dados?.rows) ? dados.rows : [];
  });
}

export function useMoedasCheque() {
  return useQuery(
    ['oktonbank-moedas-cheque'],
    async () => {
      const dados = await chamarOktonBank<{ rows: MoedaCheque[] }>(
        'OBFinanceiro',
        'listarMoedasCheque',
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { staleTime: 1000 * 60 * 5 },
  );
}
